import { defineStore } from "pinia";
import type { Component } from "vue";

export const useLayoutStore = defineStore("layout", () => {
  const widgetScroll = ref(false);

  /** When true, the parent layout should not render an outline around the content */
  const omitOutline = ref(false);

  /** Side panel state for canvas editor or other panels */
  const sidePanelVisible = ref(false);
  const sidePanelComponent = shallowRef<Component | null>(null);
  const sidePanelProps = ref<Record<string, unknown>>({});

  /** Widget to jump to in editor (cleared after use) */
  const jumpToWidget = ref<string | null>(null);

  /** Widget to add to AI context panel (cleared after use) */
  const addWidgetToAiContext = ref<string | null>(null);

  /** Which tab is currently active in the canvas editor (null when editor is closed) */
  const activeEditorTab = ref<string | null>(null);

  /**
   * Widget whose data source offers a config editor, asking for it to be opened (cleared after use).
   *
   * The button lives on the widget but the config only exists in the editor panel, so the request
   * travels rather than the config — the same way `addWidgetToAiContext` does.
   */
  const editWidgetConfigurator = ref<string | null>(null);

  function openSidePanel(component: Component, props: Record<string, unknown> = {}) {
    sidePanelComponent.value = component;
    sidePanelProps.value = props;
    sidePanelVisible.value = true;
  }

  /**
   * Hide the panel without destroying the mounted component.
   * Use this for fold/unfold so the component's state (AI conversation, undo
   * stack, etc.) is preserved. Call `closeSidePanel` when navigating away to
   * fully destroy the component.
   */
  function hideSidePanel() {
    sidePanelVisible.value = false;
  }

  function closeSidePanel() {
    sidePanelVisible.value = false;
    sidePanelComponent.value = null;
    sidePanelProps.value = {};
    jumpToWidget.value = null;
  }

  function requestJumpToWidget(widgetName: string) {
    jumpToWidget.value = widgetName;
  }

  function clearJumpToWidget() {
    jumpToWidget.value = null;
  }

  function requestAddWidgetToAiContext(widgetName: string) {
    addWidgetToAiContext.value = widgetName;
  }

  function clearAddWidgetToAiContext() {
    addWidgetToAiContext.value = null;
  }

  function requestEditWidgetConfigurator(widgetName: string) {
    editWidgetConfigurator.value = widgetName;
  }

  function clearEditWidgetConfigurator() {
    editWidgetConfigurator.value = null;
  }

  function setActiveEditorTab(tab: string | null) {
    activeEditorTab.value = tab;
  }

  return {
    widgetScroll,
    omitOutline,
    sidePanelVisible,
    sidePanelComponent,
    sidePanelProps,
    jumpToWidget,
    addWidgetToAiContext,
    editWidgetConfigurator,
    activeEditorTab,
    openSidePanel,
    hideSidePanel,
    closeSidePanel,
    requestJumpToWidget,
    clearJumpToWidget,
    requestAddWidgetToAiContext,
    clearAddWidgetToAiContext,
    requestEditWidgetConfigurator,
    clearEditWidgetConfigurator,
    setActiveEditorTab,
  };
});
